import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from '../../../components/ui-components/Toast';
import RightSheet from "../../../components/ui-components/RightSheet";
import StatusPill from "./StatusPill";
import FormButton from "../../../components/form-components/FormButton";
import { MoneyInput } from "../../../components/form-components/MoneyInput";
import { Dropdown } from "../../../components/form-components/Dropdown";
import TextArea from "../../../components/form-components/TextArea";
import { formatMoney } from "../utils/allocationUtils";
import { formatRelativeTime } from "../formatters";
import {
  getPeriodVersions,
  createDraftPeriodVersion,
  submitPeriodVersion,
} from "../api";

// ── Constants ────────────────────────────────────────────────────

const REASON_OPTIONS = [
  { value: "reforecast", label: "Reforecast adjustment" },
  { value: "scope_change", label: "Scope change" },
  { value: "budget_cut", label: "Budget cut" },
  { value: "budget_increase", label: "Budget increase" },
  { value: "other", label: "Other" },
];

const APPLY_SCOPE_OPTIONS = [
  { value: "this_only", label: "This period only" },
  { value: "this_and_future", label: "This and future periods" },
];

const PERIOD_STATUS_MAP = {
  active: { label: "Active", key: "active" },
  pending_approval: { label: "Pending approval", key: "pending_approval" },
  draft_change: { label: "Draft change", key: "draft" },
  not_set: { label: "Not set", key: "archived" },
};

const VERSION_STATUS_MAP = {
  draft: "draft",
  pending: "pending_approval",
  approved: "active",
  active: "active",
  superseded: "archived",
  archived: "archived",
  rejected: "rejected",
};

// ── Version History List ─────────────────────────────────────────
//
// Versions render as rows inside a single bordered list container (see
// VersionHistoryList) rather than as individual cards — one outline, divided
// rows, less visual fragmentation. The active version is marked with a soft
// tint and a 3px left indicator, not a competing colored card.

function VersionHistoryRow({ version, isActive }) {
  const [expanded, setExpanded] = useState(false);
  const statusKey = VERSION_STATUS_MAP[version.status] || "archived";
  const isRejected = version.status === "rejected";
  const hasDetails = !!(version.change_reason || version.notes || (isRejected && version.approval_request_id));

  const toggle = () => hasDetails && setExpanded((e) => !e);

  return (
    <div
      className={isActive ? "bg-success-soft" : "bg-card"}
      style={isActive ? { boxShadow: "inset 3px 0 0 hsl(var(--success))" } : undefined}
    >
      <div
        className={`flex items-center justify-between gap-3 px-4 py-3.5 ${
          hasDetails ? "cursor-pointer hover:bg-surface-2" : ""
        }`}
        style={{ minHeight: 56 }}
        role={hasDetails ? "button" : undefined}
        tabIndex={hasDetails ? 0 : undefined}
        aria-expanded={hasDetails ? expanded : undefined}
        onClick={toggle}
        onKeyDown={(e) => {
          if (hasDetails && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <StatusPill status={statusKey} />
          <span className="text-sm font-medium text-text tabular-nums truncate">
            {formatMoney(version.amount_cents, version.currency || "EUR")}
          </span>
        </div>
        <div className="flex items-center gap-2.5 flex-shrink-0">
          <span className="text-xs text-text-2">
            {formatRelativeTime(version.created_at)}
          </span>
          {hasDetails && (
            <FontAwesomeIcon
              icon={icon({ name: "chevron-down", style: "solid" })}
              className={`text-[10px] text-text-3 transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          )}
        </div>
      </div>
      {expanded && hasDetails && (
        <div className="px-4 pb-3.5 -mt-1 text-xs text-text-2 space-y-1">
          {version.change_reason && (
            <p>
              <span className="font-medium text-text">Reason:</span> {version.change_reason}
            </p>
          )}
          {version.notes && (
            <p>
              <span className="font-medium text-text">Notes:</span> {version.notes}
            </p>
          )}
          {isRejected && version.approval_request_id && (
            <p>
              <Link to={`/approvals/${version.approval_request_id}`} className="text-danger underline font-medium">
                View rejection details
              </Link>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

VersionHistoryRow.propTypes = {
  version: PropTypes.object.isRequired,
  isActive: PropTypes.bool,
};

// VersionHistoryList — the single bordered container that holds the version rows.
function VersionHistoryList({ versions }) {
  return (
    <div className="rounded-md border border-card-bd divide-y divide-border overflow-hidden">
      {versions.map((v) => (
        <VersionHistoryRow key={v.id} version={v} isActive={v.status === "active"} />
      ))}
    </div>
  );
}

VersionHistoryList.propTypes = {
  versions: PropTypes.array.isRequired,
};

// ── Draft Editor Form ────────────────────────────────────────────

function DraftEditorForm({ draftVersion, currency, onSubmitSuccess, onCancel }) {
  const [amount, setAmount] = useState(null);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [applyScope, setApplyScope] = useState("this_only");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (draftVersion) {
      const cents = draftVersion.amount_cents || 0;
      setAmount(cents > 0 ? cents / 100 : null);
      setReason(draftVersion.change_reason || "");
      setNotes(draftVersion.notes || "");
    }
  }, [draftVersion, currency]);

  const handleSubmit = async () => {
    const cents = amount != null ? Math.round(amount * 100) : 0;
    if (cents <= 0) {
      toast.error("Amount must be greater than zero");
      return;
    }
    if (!reason) {
      toast.error("Please select a reason");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        amount_cents: cents,
        currency: currency || "EUR",
        change_reason: reason,
        notes,
        apply_scope: applyScope,
      };
      console.log('[PeriodDrawer] submitPeriodVersion', { versionId: draftVersion.id, payload });
      const result = await submitPeriodVersion(draftVersion.id, payload);
      const version = result?.version;
      console.log('[PeriodDrawer] submitPeriodVersion result', {
        versionId: draftVersion.id,
        versionStatus: version?.status,
        approvalRequestId: version?.approval_request_id,
      });
      if (version?.status === "active") {
        toast.success("Change activated");
      } else if (version?.status === "pending") {
        toast.success("Submitted for approval");
      } else {
        toast.success("Change submitted");
      }
      onSubmitSuccess();
    } catch (err) {
      // Handle 409 "budget not active" specifically
      const serverMsg = err?.serverBody?.error?.message || err?.message || "";
      if (err?.status === 409 && serverMsg.toLowerCase().includes("budget is not active")) {
        toast.error("Budget is not active. Activate the budget to make amounts effective.");
      } else {
        toast.error(serverMsg || "Failed to submit change");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!draftVersion) return null;

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="new_amount" className="block text-xs font-medium text-text-2 mb-2">New amount</label>
        <div style={{ maxWidth: "200px" }}>
          <MoneyInput
            id="new_amount"
            name="new_amount"
            value={amount}
            onChange={setAmount}
            currency={currency || "EUR"}
            locale="nl-NL"
            allowNegative={false}
            decimals={2}
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-text-2 mb-2">
          Apply to
        </label>
        <div className="flex flex-col gap-2">
          {APPLY_SCOPE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-center gap-2 text-sm ${
                opt.disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              <input
                type="radio"
                name="apply_scope"
                value={opt.value}
                checked={applyScope === opt.value}
                disabled={opt.disabled}
                onChange={(e) => setApplyScope(e.target.value)}
              />
              <span className="text-text">{opt.label}</span>
              {opt.disabled && (
                <span className="text-xs text-text-3 italic">(coming later)</span>
              )}
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-text-2 mb-1">
          Reason (required)
        </label>
        <Dropdown
          options={REASON_OPTIONS}
          selectedValue={REASON_OPTIONS.find((o) => o.value === reason) || null}
          setSelectedValue={(opt) => setReason(opt?.value || "")}
          fullWidth
        />
      </div>

      <TextArea
        isLoaded
        name="additional_notes"
        title="Additional notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Additional notes..."
        rows={2}
      />

      <div className="flex flex-row justify-end gap-2 pt-2">
        <FormButton title="Cancel" style="tertiary" onClick={onCancel} />
        <FormButton
          title={submitting ? "Submitting..." : "Submit change"}
          style="primary"
          onClick={handleSubmit}
          disabled={!reason || submitting}
        />
      </div>
    </div>
  );
}

DraftEditorForm.propTypes = {
  draftVersion: PropTypes.object,
  currency: PropTypes.string,
  onSubmitSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

// ── Inline Draft Amount Editor (for unsaved / draft-budget periods) ──

function DraftAmountEditor({ period, currency, onAmountChange, onClose }) {
  const currentCents = period?.amountCents ?? 0;
  const [amount, setAmount] = useState(currentCents > 0 ? currentCents / 100 : null);
  const [applyScope, setApplyScope] = useState("this_only");

  // Re-sync when period changes
  useEffect(() => {
    const cents = period?.amountCents ?? 0;
    setAmount(cents > 0 ? cents / 100 : null);
    setApplyScope("this_only");
  }, [period, currency]);

  const handleSave = () => {
    const cents = amount != null ? Math.round(amount * 100) : 0;
    if (onAmountChange) {
      onAmountChange(period, cents, applyScope);
    }
    onClose();
  };

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="period_amount" className="block text-xs font-medium text-text-2 mb-2">Period amount</label>
        <div style={{ maxWidth: "200px" }}>
          <MoneyInput
            id="period_amount"
            name="period_amount"
            value={amount}
            onChange={setAmount}
            currency={currency || "EUR"}
            locale="nl-NL"
            allowNegative={false}
            decimals={2}
            autoFocus
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-text-2 mb-2">
          Apply to
        </label>
        <div className="flex flex-col gap-2">
          {APPLY_SCOPE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 text-sm cursor-pointer"
            >
              <input
                type="radio"
                name="draft_apply_scope"
                value={opt.value}
                checked={applyScope === opt.value}
                onChange={(e) => setApplyScope(e.target.value)}
              />
              <span className="text-text">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex flex-row justify-end gap-2 pt-2">
        <FormButton title="Cancel" style="tertiary" onClick={onClose} />
        <FormButton title="Apply" style="primary" onClick={handleSave} />
      </div>
    </div>
  );
}

DraftAmountEditor.propTypes = {
  period: PropTypes.object,
  currency: PropTypes.string,
  onAmountChange: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

// ── Main PeriodDrawer Component ──────────────────────────────────

const PeriodDrawer = ({ period, open, onClose, currency = "EUR", budgetId, budgetStatus, onAmountChange }) => {
  const queryClient = useQueryClient();
  const [view, setView] = useState("history"); // "history" | "change"
  const [draftVersion, setDraftVersion] = useState(null);
  // Track whether we've already auto-opened the edit form for this drawer session
  const [autoOpenedForPeriod, setAutoOpenedForPeriod] = useState(null);

  const periodId = period?.period_id;
  const isBudgetDraft = budgetStatus === "draft";

  // Reset view when period changes or drawer closes
  useEffect(() => {
    if (!open) {
      setView(isBudgetDraft ? "draft_edit" : "history");
      setDraftVersion(null);
      setAutoOpenedForPeriod(null);
    }
  }, [open, periodId, isBudgetDraft]);

  // For draft budgets, always start in edit mode when opened
  useEffect(() => {
    if (open && isBudgetDraft) {
      setView("draft_edit");
    }
  }, [open, isBudgetDraft]);

  // Fetch versions (skip for draft budgets without period_id)
  const {
    data: versionsData,
    isLoading: versionsLoading,
    refetch: refetchVersions,
  } = useQuery({
    queryKey: ["periodVersions", periodId],
    queryFn: () => getPeriodVersions(periodId),
    enabled: !!periodId && open && !isBudgetDraft,
  });

  const versions = versionsData?.versions || [];
  const activeVersion = versions.find((v) => v.status === "active");
  const existingDraft = versions.find((v) => v.status === "draft");
  const hasPending = versions.some((v) => v.status === "pending");
  const pendingVersion = versions.find((v) => v.status === "pending");
  const rejectedVersion = versions.find((v) => v.status === "rejected");
  const approvalRequestId = pendingVersion?.approval_request_id ?? rejectedVersion?.approval_request_id;

  // Create draft mutation
  const createDraftMutation = useMutation({
    mutationFn: () => createDraftPeriodVersion(periodId),
    onSuccess: (data) => {
      const draft = data?.version;
      if (draft) {
        setDraftVersion(draft);
        const isDraftVersion = draft.isDraft === true;
        const shouldUseDraftEdit = isDraftVersion || isBudgetDraft;
        setView(shouldUseDraftEdit ? "draft_edit" : "change");
        refetchVersions();
      }
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to create draft");
    },
  });

  // Auto-open edit form when the budget is in draft mode (for saved budgets with period_id)
  useEffect(() => {
    if (
      !isBudgetDraft ||
      versionsLoading ||
      !open ||
      !periodId ||
      hasPending ||
      autoOpenedForPeriod === periodId
    ) {
      return;
    }
    // Mark that we've auto-opened for this period so we don't loop
    setAutoOpenedForPeriod(periodId);
    if (existingDraft) {
      setDraftVersion(existingDraft);
      setView("change");
    } else {
      createDraftMutation.mutate();
    }
  }, [isBudgetDraft, versionsLoading, open, periodId, hasPending, existingDraft, autoOpenedForPeriod, createDraftMutation]);

  const handleNewChange = useCallback(() => {
    if (existingDraft) {
      setDraftVersion(existingDraft);
      setView("change");
    } else {
      createDraftMutation.mutate();
    }
  }, [existingDraft, createDraftMutation]);

  const handleSubmitSuccess = useCallback(() => {
    setView("history");
    setDraftVersion(null);
    refetchVersions();
    // Invalidate the periods list to refresh table
    if (budgetId) {
      queryClient.invalidateQueries({ queryKey: ["budgetPeriods", budgetId] });
    }
  }, [refetchVersions, queryClient, budgetId]);

  const handleCancelChange = useCallback(() => {
    setView("history");
    setDraftVersion(null);
  }, []);

  // Derive period status label/badge
  const periodStatusKey = period?.status
    ? PERIOD_STATUS_MAP[period.status]?.key || "archived"
    : "archived";

  // Budget-status gating
  const isReadOnly = ["archived", "pending_approval"].includes(budgetStatus);
  const canEditDrafts = budgetStatus === "draft" || budgetStatus === "active" || budgetStatus === "paused";

  // Determine CTA label
  let ctaLabel = "New change";
  let ctaDisabled = false;
  if (existingDraft) {
    ctaLabel = "Edit draft";
  }
  if (hasPending) {
    ctaLabel = "Awaiting approval";
    ctaDisabled = true;
  }
  if (isReadOnly) {
    ctaDisabled = true;
  }

  const title = period?.label || "Period";
  const allocatedCents = period?.active_amount_cents != null
    ? period.active_amount_cents
    : period?.amountCents != null && period.amountCents > 0
    ? period.amountCents
    : null;
  const subtitle = allocatedCents != null
    ? `Allocated amount · ${formatMoney(allocatedCents, currency)}`
    : "No amount allocated yet";

  // The primary action lives in the sticky footer — but only when the drawer is
  // actually editable. Read-only budget statuses show no footer (spec §7).
  const historyFooter =
    view === "history" && !isBudgetDraft && canEditDrafts ? (
      <RightSheet.Footer
        primary={{
          label: ctaLabel,
          onClick: handleNewChange,
          disabled: ctaDisabled || createDraftMutation.isPending,
          loading: createDraftMutation.isPending,
        }}
        loadingLabel="Creating..."
      />
    ) : null;

  return (
    <RightSheet
      open={open}
      onClose={onClose}
      eyebrow="Budget period"
      title={title}
      subtitle={subtitle}
      footer={historyFooter}
    >
      {/* Inline notifications — budget-status context + any approval lock. One
          consistent notification style, grouped with even spacing (spec §6). */}
      {(budgetStatus === "draft" ||
        budgetStatus === "pending_approval" ||
        budgetStatus === "paused" ||
        budgetStatus === "archived" ||
        (hasPending && approvalRequestId)) && (
        <div className="space-y-2.5 mb-7">
          {budgetStatus === "draft" && (
            <div className="px-3.5 py-2.5 bg-surface-2 border border-border rounded-md text-xs text-text-2">
              Draft budget &mdash; amounts are not enforced until activation.
            </div>
          )}
          {budgetStatus === "pending_approval" && (
            <div className="px-3.5 py-2.5 bg-warning-soft border border-warning rounded-md text-xs text-warning">
              Awaiting budget approval &mdash; amounts cannot be activated.
            </div>
          )}
          {budgetStatus === "paused" && (
            <div className="px-3.5 py-2.5 bg-warning-soft border border-warning rounded-md text-xs text-warning">
              Budget is paused &mdash; changes can be prepared but not activated.
            </div>
          )}
          {budgetStatus === "archived" && (
            <div className="px-3.5 py-2.5 bg-surface-2 border border-border rounded-md text-xs text-text-2">
              Archived &mdash; read only.
            </div>
          )}
          {hasPending && approvalRequestId && (
            <div className="px-3.5 py-2.5 bg-warning-soft border border-warning rounded-md text-xs text-warning">
              Locked &mdash; pending approval.{" "}
              <Link to={`/approvals/${approvalRequestId}`} className="underline font-medium">
                View approval request
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Draft budget: inline local amount editor (no backend API needed) */}
      {view === "draft_edit" && isBudgetDraft && (
        <RightSheet.Section title="Edit period amount">
          <DraftAmountEditor
            period={period}
            currency={currency}
            onAmountChange={onAmountChange}
            onClose={onClose}
          />
        </RightSheet.Section>
      )}

      {view === "history" && !isBudgetDraft && (
        <RightSheet.Section
          title="Version history"
          actions={<StatusPill status={periodStatusKey} />}
        >
          {existingDraft && !hasPending && canEditDrafts && (
            <p className="text-xs text-primary mb-2">Draft change in progress</p>
          )}
          {versionsLoading && (
            <div className="rounded-md border border-card-bd divide-y divide-border overflow-hidden">
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ height: 56 }} className="bg-surface-2 animate-pulse" />
              ))}
            </div>
          )}
          {!versionsLoading && versions.length === 0 && (
            <div className="rounded-md border border-dashed border-card-bd px-4 py-6 text-center text-sm text-text-2">
              No versions yet. Use &ldquo;New change&rdquo; to create the first one.
            </div>
          )}
          {!versionsLoading && versions.length > 0 && <VersionHistoryList versions={versions} />}
        </RightSheet.Section>
      )}

      {view === "change" && !isBudgetDraft && (
        <RightSheet.Section title={existingDraft ? "Edit draft change" : "New change"}>
          {activeVersion && (
            <div className="mb-4">
              <label
                htmlFor="current_active_amount"
                className="block text-xs font-medium text-text-2 mb-2"
              >
                Current active amount
              </label>
              <div style={{ maxWidth: "200px" }}>
                <MoneyInput
                  id="current_active_amount"
                  name="current_active_amount"
                  value={activeVersion.amount_cents / 100}
                  onChange={() => {}}
                  currency={currency || "EUR"}
                  locale="nl-NL"
                  readOnly
                  inputClassName="text-sm text-text-2"
                />
              </div>
            </div>
          )}
          <DraftEditorForm
            draftVersion={draftVersion}
            currency={currency}
            onSubmitSuccess={handleSubmitSuccess}
            onCancel={handleCancelChange}
          />
        </RightSheet.Section>
      )}
    </RightSheet>
  );
};

PeriodDrawer.propTypes = {
  period: PropTypes.shape({
    period_id: PropTypes.number,
    label: PropTypes.string,
    active_amount_cents: PropTypes.number,
    amountCents: PropTypes.number,
    status: PropTypes.string,
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currency: PropTypes.string,
  budgetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  budgetStatus: PropTypes.string,
  onAmountChange: PropTypes.func,
};

export default PeriodDrawer;
